
import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Input,
    InputNumber,
    Layout,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
} from "antd";

import {
    DeleteOutlined,
    EditOutlined,
    FilePdfOutlined,
    PlusOutlined,
    PrinterOutlined,
    ReloadOutlined,
    SearchOutlined,
    WalletOutlined,
    DollarOutlined,
    TeamOutlined,
    RiseOutlined,
} from "@ant-design/icons";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

import Swal from "sweetalert2";
import { PDFDownloadLink } from "@react-pdf/renderer";

import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";
import SalaryAdvance from "./SalaryAdvance";
import { useAcademicYear } from "../../AcademicYearContext";
import api from "../../api/api";
import "./Payroll.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const MONTHS = [
    "January", "February", "March", "April",
    "May", "June", "July", "August",
    "September", "October", "November", "December",
];

const initialFormData = {
    teacher_id: null,
    support_staff_id: null,
    month: "",
    basic_salary: 0,
    night_school_commission: 0,
    salary_advance: 0,
    salary_advance_record: null,
    salary_advance_balance: 0,
    income_tax: 0,
    salary_deduction: 0,
    salary_deduction_reason: "",
    total_deductions: 0,
};

// Escape user-entered text before inserting it into printable HTML.
const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (character) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
    }[character]));

function Payroll() {
    const [salaryAdvanceBalance, setSalaryAdvanceBalance] = useState(0);
    const [salaryAdvanceRecord, setSalaryAdvanceRecord] = useState(null);
    const [salaryAdvanceLoading, setSalaryAdvanceLoading] = useState(false);

    const { selectedYear } = useAcademicYear();

    const [form] = Form.useForm();

    const [payrolls, setPayrolls] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [supportStaff, setSupportStaff] = useState([]);

    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [editingPayroll, setEditingPayroll] = useState(null);
    const [showSalaryAdvance, setShowSalaryAdvance] = useState(false);

    const [filters, setFilters] = useState({
        search: "",
        month: "",
    });

    const [selectedStaffType, setSelectedStaffType] = useState(null);

    /*
    |--------------------------------------------------------------------------
    | FETCH SALARY ADVANCE BALANCE
    |--------------------------------------------------------------------------
    */

    const fetchSalaryAdvanceBalance = async ({
        teacherId = null,
        supportStaffId = null,
        month = null,
    } = {}) => {
        if (
            !selectedYear?.id ||
            !month ||
            (!teacherId && !supportStaffId)
        ) {
            return;
        }

        setSalaryAdvanceLoading(true);

        try {
            const response = await api.get(
                "/salary-advance-balance/",
                {
                    params: {
                        teacher_id: teacherId || undefined,
                        support_staff_id: supportStaffId || undefined,
                        month,
                        academic_year: selectedYear.id,
                    },
                }
            );

            const balance = Number(
                response.data?.remaining_balance || 0
            );

            const recordId =
                response.data?.salary_advance_id || null;

            setSalaryAdvanceBalance(balance);
            setSalaryAdvanceRecord(recordId);

            form.setFieldsValue({
                salary_advance: balance,
                salary_advance_record: recordId,
            });
        } catch (error) {
            console.error(
                "SALARY ADVANCE ERROR:",
                error.response?.data || error
            );

            setSalaryAdvanceBalance(0);
            setSalaryAdvanceRecord(null);

            form.setFieldsValue({
                salary_advance: 0,
                salary_advance_record: null,
            });
        } finally {
            setSalaryAdvanceLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | FETCH PAYROLLS
    |--------------------------------------------------------------------------
    */

    const fetchPayrolls = async () => {
        if (!selectedYear?.id) return;

        setTableLoading(true);

        try {
            const response = await api.get("/payrolls/", {
                params: {
                    academic_year: selectedYear.id,
                },
            });

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setPayrolls(data);
        } catch (error) {
            console.error(
                "PAYROLL FETCH ERROR:",
                error.response?.data || error
            );

            message.error("Failed to load payroll records.");
        } finally {
            setTableLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | FETCH TEACHERS
    |--------------------------------------------------------------------------
    */

    const fetchTeachers = async () => {
        try {
            const response = await api.get(
                "/api/users/teacher-data/"
            );

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setTeachers(data);
        } catch (error) {
            console.error("TEACHERS FETCH ERROR:", error);
            message.error("Failed to load teachers.");
        }
    };

    /*
    |--------------------------------------------------------------------------
    | FETCH SUPPORT STAFF
    |--------------------------------------------------------------------------
    */

    const fetchSupportStaff = async () => {
        try {
            const response = await api.get("/supportstaff/");

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setSupportStaff(data);
        } catch (error) {
            console.error("SUPPORT STAFF FETCH ERROR:", error);
            message.error("Failed to load support staff.");
        }
    };

    /*
    |--------------------------------------------------------------------------
    | EFFECTS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (selectedYear?.id) {
            fetchPayrolls();
        }
    }, [selectedYear?.id]);

    useEffect(() => {
        fetchTeachers();
        fetchSupportStaff();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | FORMAT MONEY
    |--------------------------------------------------------------------------
    */

    const formatMoney = (value) => {
        const number = parseFloat(value || 0);

        return `$${number.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    /*
    |--------------------------------------------------------------------------
    | STAFF NAME
    |--------------------------------------------------------------------------
    */

    const getStaffName = (payroll) => {
        return (
            payroll.teacher?.full_name ||
            payroll.support_staff?.full_name ||
            "Unknown Staff"
        );
    };

    /*
    |--------------------------------------------------------------------------
    | OPEN CREATE MODAL
    |--------------------------------------------------------------------------
    */

    const openCreateModal = () => {
        setEditingPayroll(null);
        setSelectedStaffType(null);

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        form.resetFields();

        form.setFieldsValue({
            ...initialFormData,
        });

        setOpenModal(true);
    };

    /*
    |--------------------------------------------------------------------------
    | OPEN EDIT MODAL
    |--------------------------------------------------------------------------
    */

    const openEditModal = (payroll) => {
        setEditingPayroll(payroll);

        const teacherId = payroll.teacher?.id || null;
        const supportStaffId = payroll.support_staff?.id || null;

        setSelectedStaffType(
            teacherId
                ? "teacher"
                : supportStaffId
                    ? "support_staff"
                    : null
        );

        setSalaryAdvanceBalance(0);

        setSalaryAdvanceRecord(
            payroll.salary_advance_record || null
        );

        form.setFieldsValue({
            teacher_id: teacherId,
            support_staff_id: supportStaffId,

            month: payroll.month || "",

            basic_salary: Number(payroll.basic_salary || 0),

            night_school_commission: Number(
                payroll.night_school_commission || 0
            ),

            salary_advance: Number(
                payroll.salary_advance || 0
            ),

            salary_advance_record:
                payroll.salary_advance_record || null,

            income_tax: Number(payroll.income_tax || 0),

            salary_deduction: Number(
                payroll.salary_deduction || 0
            ),

            // NEW: Load the previously saved deduction reason.
            salary_deduction_reason:
                payroll.salary_deduction_reason || "",

            total_deductions: Number(
                payroll.total_deductions || 0
            ),
        });

        setOpenModal(true);
    };

    /*
    |--------------------------------------------------------------------------
    | CLOSE MODAL
    |--------------------------------------------------------------------------
    */

    const closeModal = () => {
        setOpenModal(false);
        setEditingPayroll(null);
        setSelectedStaffType(null);

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        form.resetFields();
    };

    /*
    |--------------------------------------------------------------------------
    | GET TEACHER SALARY
    |--------------------------------------------------------------------------
    */

    const fetchTeacherSalary = async (teacherId) => {
        if (!teacherId) return;

        try {
            const response = await api.get(
                `/teacher-salary/${teacherId}/`
            );

            form.setFieldValue(
                "basic_salary",
                Number(response.data.salary || 0)
            );
        } catch (error) {
            console.error("SALARY FETCH ERROR:", error);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | TEACHER CHANGE
    |--------------------------------------------------------------------------
    */

    const handleTeacherChange = async (teacherId) => {
        setSelectedStaffType(
            teacherId ? "teacher" : null
        );

        const currentMonth = form.getFieldValue("month");

        form.setFieldsValue({
            teacher_id: teacherId,
            support_staff_id: null,
            salary_advance: 0,
            salary_advance_record: null,
        });

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        const teacher = teachers.find(
            (item) => item.id === teacherId
        );

        if (teacher) {
            form.setFieldsValue({
                basic_salary: Number(teacher.salary || 0),
            });
        }

        if (teacherId && currentMonth) {
            await fetchSalaryAdvanceBalance({
                teacherId,
                month: currentMonth,
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | SUPPORT STAFF CHANGE
    |--------------------------------------------------------------------------
    */

    const handleSupportStaffChange = async (staffId) => {
        setSelectedStaffType(
            staffId ? "support_staff" : null
        );

        const currentMonth = form.getFieldValue("month");

        const staff = supportStaff.find(
            (item) => item.id === staffId
        );

        form.setFieldsValue({
            support_staff_id: staffId || null,
            teacher_id: null,
            basic_salary: Number(staff?.salary || 0),
            salary_advance: 0,
            salary_advance_record: null,
        });

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        if (staffId && currentMonth) {
            await fetchSalaryAdvanceBalance({
                supportStaffId: staffId,
                month: currentMonth,
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | MONTH CHANGE
    |--------------------------------------------------------------------------
    */

    const handleMonthChange = async (month) => {
        const teacherId = form.getFieldValue("teacher_id");
        const supportStaffId = form.getFieldValue("support_staff_id");

        if (teacherId) {
            await fetchSalaryAdvanceBalance({
                teacherId,
                month,
            });
        }

        if (supportStaffId) {
            await fetchSalaryAdvanceBalance({
                supportStaffId,
                month,
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | SUBMIT PAYROLL
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (values) => {
        if (!values.teacher_id && !values.support_staff_id) {
            message.error(
                "Please select a teacher or support staff."
            );
            return;
        }

        if (!selectedYear?.id) {
            message.error("Please select an academic year.");
            return;
        }

        const deduction = Number(
            values.salary_advance || 0
        );

        const outstandingBalance = Number(
            salaryAdvanceBalance || 0
        );

        if (
            !editingPayroll &&
            deduction > outstandingBalance
        ) {
            message.error(
                "Salary advance deduction cannot be greater than the outstanding balance."
            );
            return;
        }

        // NEW: Normalize the deduction reason.
        const salaryDeduction = Number(
            values.salary_deduction || 0
        );

        const deductionReason = String(
            values.salary_deduction_reason || ""
        ).trim();

        if (salaryDeduction > 0 && !deductionReason) {
            message.error(
                "Please provide a reason for the salary deduction."
            );
            return;
        }

        setLoading(true);

        try {
            const payload = {
                teacher_id: values.teacher_id || null,

                support_staff_id:
                    values.support_staff_id || null,

                month: values.month,

                basic_salary: Number(
                    values.basic_salary || 0
                ),

                night_school_commission: Number(
                    values.night_school_commission || 0
                ),

                salary_advance: deduction,

                salary_advance_record:
                    values.salary_advance_record ||
                    salaryAdvanceRecord ||
                    null,

                income_tax: Number(
                    values.income_tax || 0
                ),

                salary_deduction: salaryDeduction,

                // NEW: Send the reason to Django REST Framework.
                salary_deduction_reason: deductionReason,
            };

            const response = editingPayroll
                ? await api.put(
                    `/payrolls/${editingPayroll.id}/`,
                    payload
                )
                : await api.post(
                    "/payrolls/",
                    payload
                );

            if (editingPayroll) {
                setPayrolls((previous) =>
                    previous.map((item) =>
                        item.id === editingPayroll.id
                            ? response.data
                            : item
                    )
                );

                message.success(
                    "Payroll updated successfully."
                );
            } else {
                setPayrolls((previous) => [
                    response.data,
                    ...previous,
                ]);

                message.success(
                    "Payroll created successfully."
                );
            }

            closeModal();
        } catch (error) {
            console.error(
                "PAYROLL SUBMIT ERROR:",
                error.response?.data || error
            );

            Swal.fire({
                icon: "error",
                title: "Payroll Error",
                text:
                    typeof error.response?.data === "object"
                        ? JSON.stringify(error.response.data)
                        : (
                            error.response?.data ||
                            "Unable to save payroll."
                        ),
            });
        } finally {
            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | DELETE PAYROLL
    |--------------------------------------------------------------------------
    */

    const handleDelete = async (id) => {
        const confirmation = await Swal.fire({
            title: "Delete Payroll?",
            text: "This payroll record will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            confirmButtonColor: "#f97316",
        });

        if (!confirmation.isConfirmed) return;

        try {
            await api.delete(`/payrolls/${id}/`);

            setPayrolls((previous) =>
                previous.filter((item) => item.id !== id)
            );

            message.success("Payroll deleted successfully.");
        } catch (error) {
            console.error(error);
            message.error("Failed to delete payroll.");
        }
    };

    /*
    |--------------------------------------------------------------------------
    | PRINT PAYROLL
    |--------------------------------------------------------------------------
    */

    const handlePrint = (payroll) => {
        const newWindow = window.open("", "_blank");

        if (!newWindow) {
            message.error(
                "Please allow pop-ups to print payroll."
            );
            return;
        }

        const staffName = getStaffName(payroll);

        const deductionReason = String(
            payroll.salary_deduction_reason || ""
        ).trim();

        // Only print the reason when there is an actual deduction.
        const deductionReasonRow =
            Number(payroll.salary_deduction || 0) > 0 &&
            deductionReason
                ? `
                    <tr>
                        <th>Salary Deduction Reason</th>
                        <td class="reason">
                            ${escapeHtml(deductionReason)}
                        </td>
                    </tr>
                `
                : "";

        newWindow.document.write(`
            <html>
                <head>
                    <title>Payroll - ${escapeHtml(staffName)}</title>

                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            padding: 30px;
                            color: #111;
                        }

                        h1 {
                            text-align: center;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 25px;
                        }

                        th, td {
                            border: 1px solid #ddd;
                            padding: 12px;
                            text-align: left;
                        }

                        th {
                            background: #111;
                            color: white;
                            width: 40%;
                        }

                        .total {
                            font-weight: bold;
                            font-size: 18px;
                        }

                        .reason {
                            color: #b91c1c;
                            white-space: pre-wrap;
                            line-height: 1.6;
                        }
                    </style>
                </head>

                <body>
                    <h1>Payroll Slip</h1>

                    <p>
                        <strong>Staff:</strong>
                        ${escapeHtml(staffName)}
                    </p>

                    <p>
                        <strong>Month:</strong>
                        ${escapeHtml(payroll.month)}
                    </p>

                    <p>
                        <strong>Academic Year:</strong>
                        ${escapeHtml(payroll.academic_year?.name || "")}
                    </p>

                    <table>
                        <tr>
                            <th>Basic Salary</th>
                            <td>${formatMoney(payroll.basic_salary)}</td>
                        </tr>

                        <tr>
                            <th>Bonus / Compensation</th>
                            <td>${formatMoney(payroll.night_school_commission)}</td>
                        </tr>

                        <tr>
                            <th>Salary Advance</th>
                            <td>${formatMoney(payroll.salary_advance)}</td>
                        </tr>

                        <tr>
                            <th>Income Tax</th>
                            <td>${formatMoney(payroll.income_tax)}</td>
                        </tr>

                        <tr>
                            <th>Salary Deduction</th>
                            <td>${formatMoney(payroll.salary_deduction)}</td>
                        </tr>

                        ${deductionReasonRow}

                        <tr>
                            <th>Total Deductions</th>
                            <td>${formatMoney(payroll.total_deductions)}</td>
                        </tr>

                        <tr class="total">
                            <th>Total Paid</th>
                            <td>${formatMoney(payroll.total_paid)}</td>
                        </tr>
                    </table>
                </body>
            </html>
        `);

        newWindow.document.close();
        newWindow.focus();
        newWindow.print();
    };

    /*
    |--------------------------------------------------------------------------
    | FILTERED PAYROLLS
    |--------------------------------------------------------------------------
    */

    const filteredPayrolls = useMemo(() => {
        return payrolls.filter((payroll) => {
            const staffName = getStaffName(payroll);

            const matchesSearch =
                !filters.search ||
                staffName.toLowerCase().includes(
                    filters.search.toLowerCase()
                );

            const matchesMonth =
                !filters.month ||
                payroll.month?.toLowerCase() ===
                filters.month.toLowerCase();

            return matchesSearch && matchesMonth;
        });
    }, [payrolls, filters]);

    /*
    |--------------------------------------------------------------------------
    | STATISTICS
    |--------------------------------------------------------------------------
    */

    const statistics = useMemo(() => {
        const totalPayroll = filteredPayrolls.reduce(
            (total, item) =>
                total + Number(item.total_paid || 0),
            0
        );

        const totalDeductions = filteredPayrolls.reduce(
            (total, item) =>
                total + Number(item.total_deductions || 0),
            0
        );

        const totalAdvances = filteredPayrolls.reduce(
            (total, item) =>
                total + Number(item.salary_advance || 0),
            0
        );

        return {
            totalPayroll,
            totalDeductions,
            totalAdvances,
            staffCount: filteredPayrolls.length,
        };
    }, [filteredPayrolls]);

    /*
    |--------------------------------------------------------------------------
    | CHART DATA
    |--------------------------------------------------------------------------
    */

    const chartData = useMemo(() => {
        const grouped = filteredPayrolls.reduce(
            (accumulator, item) => {
                const key = item.month;

                if (!accumulator[key]) {
                    accumulator[key] = {
                        month: key,
                        total: 0,
                    };
                }

                accumulator[key].total += Number(
                    item.total_paid || 0
                );

                return accumulator;
            },
            {}
        );

        return Object.values(grouped);
    }, [filteredPayrolls]);

    /*
    |--------------------------------------------------------------------------
    | TABLE COLUMNS
    |--------------------------------------------------------------------------
    */

    const columns = [
        {
            title: "Staff Name",
            key: "staff",
            fixed: "left",
            width: 220,
            render: (_, record) => (
                <div className="staff-cell">
                    <div className="staff-avatar">
                        {getStaffName(record)
                            .charAt(0)
                            .toUpperCase()}
                    </div>

                    <div>
                        <Text strong>
                            {getStaffName(record)}
                        </Text>

                        <br />

                        <Text
                            type="secondary"
                            className="staff-type"
                        >
                            {record.teacher
                                ? "Teacher"
                                : "Support Staff"}
                        </Text>
                    </div>
                </div>
            ),
        },

        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            width: 120,
            render: (month) => (
                <Tag color="orange">{month}</Tag>
            ),
        },

        {
            title: "Academic Year",
            key: "academic_year",
            width: 150,
            render: (_, record) =>
                record.academic_year?.name || "-",
        },

        {
            title: "Basic Salary",
            dataIndex: "basic_salary",
            key: "basic_salary",
            width: 140,
            render: (value) => formatMoney(value),
        },

        {
            title: "Bonus",
            dataIndex: "night_school_commission",
            key: "bonus",
            width: 140,
            render: (value) => formatMoney(value),
        },

        {
            title: "Deductions",
            dataIndex: "total_deductions",
            key: "deductions",
            width: 140,
            render: (value) => (
                <Text type="danger" strong>
                    {formatMoney(value)}
                </Text>
            ),
        },

        {
            title: "Advance",
            dataIndex: "salary_advance",
            key: "advance",
            width: 140,
            render: (value) => (
                <Text type="danger" strong>
                    {formatMoney(value)}
                </Text>
            ),
        },

        {
            title: "Income Tax",
            dataIndex: "income_tax",
            key: "income_tax",
            width: 140,
            render: (value) => formatMoney(value),
        },

        {
            title: "Salary Deduction",
            dataIndex: "salary_deduction",
            key: "salary_deduction",
            width: 150,
            render: (value) => formatMoney(value),
        },

        // NEW: Deduction reason column.
        {
            title: "Deduction Reason",
            dataIndex: "salary_deduction_reason",
            key: "salary_deduction_reason",
            width: 260,
            render: (value, record) => {
                if (
                    Number(record.salary_deduction || 0) <= 0 ||
                    !value?.trim()
                ) {
                    return (
                        <Text type="secondary">
                            No reason provided
                        </Text>
                    );
                }

                return (
                    <Text
                        type="danger"
                        ellipsis={{
                            tooltip: value,
                        }}
                        style={{
                            maxWidth: 230,
                            display: "inline-block",
                        }}
                    >
                        {value}
                    </Text>
                );
            },
        },

        {
            title: "Total Paid",
            dataIndex: "total_paid",
            key: "total_paid",
            width: 150,
            render: (value) => (
                <Text strong className="total-paid">
                    {formatMoney(value)}
                </Text>
            ),
        },

        {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(record)}
                    />

                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                    />

                    <Button
                        type="text"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrint(record)}
                    />
                </Space>
            ),
        },
    ];

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <Layout className="payroll-page">
            <Content className="payroll-content">

                {/* HEADER */}

                <div className="payroll-header">
                    <div>
                        <Text className="eyebrow">
                            FINANCE / PAYROLL
                        </Text>

                        <Title
                            level={2}
                            className="page-title"
                        >
                            Staff Payroll Management
                        </Title>

                        <Text type="secondary">
                            Manage staff compensation, deductions,
                            salary advances and payroll reporting.
                        </Text>
                    </div>

                    <div className="year-badge">
                        <Text>Academic Year</Text>

                        <strong>
                            {selectedYear?.name || "Not Selected"}
                        </strong>
                    </div>
                </div>

                {/* STATISTICS */}

                <Row
                    gutter={[16, 16]}
                    className="stats-row"
                >
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card orange-card">
                            <Statistic
                                title="Total Payroll"
                                value={statistics.totalPayroll}
                                precision={2}
                                prefix="$"
                            />
                            <DollarOutlined className="stat-icon" />
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card dark-card">
                            <Statistic
                                title="Staff Records"
                                value={statistics.staffCount}
                                prefix={<TeamOutlined />}
                            />
                            <TeamOutlined className="stat-icon" />
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card light-card">
                            <Statistic
                                title="Total Deductions"
                                value={statistics.totalDeductions}
                                precision={2}
                                prefix="$"
                            />
                            <RiseOutlined className="stat-icon" />
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card black-card">
                            <Statistic
                                title="Salary Advances"
                                value={statistics.totalAdvances}
                                precision={2}
                                prefix="$"
                            />
                            <WalletOutlined className="stat-icon" />
                        </Card>
                    </Col>
                </Row>

                {/* SALARY ADVANCE */}

                {showSalaryAdvance ? (
                    <Card>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() =>
                                setShowSalaryAdvance(false)
                            }
                            className="back-button"
                        >
                            Back To Payroll
                        </Button>

                        <SalaryAdvance />
                    </Card>
                ) : (
                    <>
                        {/* TOOLBAR */}

                        <Card className="toolbar-card">
                            <Row
                                gutter={[12, 12]}
                                align="middle"
                            >
                                <Col xs={24} md={8}>
                                    <Input
                                        size="large"
                                        prefix={<SearchOutlined />}
                                        placeholder="Search staff name..."
                                        value={filters.search}
                                        onChange={(event) =>
                                            setFilters((previous) => ({
                                                ...previous,
                                                search: event.target.value,
                                            }))
                                        }
                                        allowClear
                                    />
                                </Col>

                                <Col xs={24} sm={12} md={5}>
                                    <Select
                                        size="large"
                                        placeholder="Filter by month"
                                        value={
                                            filters.month || undefined
                                        }
                                        onChange={(value) =>
                                            setFilters((previous) => ({
                                                ...previous,
                                                month: value || "",
                                            }))
                                        }
                                        allowClear
                                        options={MONTHS.map((month) => ({
                                            label: month,
                                            value: month,
                                        }))}
                                        style={{ width: "100%" }}
                                    />
                                </Col>

                                <Col xs={24} sm={12} md={11}>
                                    <Space
                                        wrap
                                        className="toolbar-actions"
                                    >
                                        <Button
                                            icon={<WalletOutlined />}
                                            onClick={() =>
                                                setShowSalaryAdvance(true)
                                            }
                                        >
                                            Salary Advances
                                        </Button>

                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={openCreateModal}
                                        >
                                            Create Payroll
                                        </Button>

                                        <PDFDownloadLink
                                            document={
                                                <PayrollSlipGenerationPDF
                                                    payrolls={filteredPayrolls}
                                                    month={filters.month}
                                                    academicYear={
                                                        selectedYear?.name
                                                    }
                                                />
                                            }
                                            fileName={`Payslips_${
                                                filters.month || "All_Months"
                                            }_${
                                                selectedYear?.name ||
                                                "Academic_Year"
                                            }.pdf`}
                                        >
                                            {({ loading }) => (
                                                <Button
                                                    icon={<FilePdfOutlined />}
                                                    danger
                                                >
                                                    {loading
                                                        ? "Generating..."
                                                        : "Download PDF"}
                                                </Button>
                                            )}
                                        </PDFDownloadLink>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>

                        {/* TABLE */}

                        <Card className="table-card">
                            <Table
                                rowKey="id"
                                loading={tableLoading}
                                columns={columns}
                                dataSource={filteredPayrolls}
                                scroll={{ x: 1900 }}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    pageSizeOptions: [
                                        "10",
                                        "20",
                                        "50",
                                        "100",
                                    ],
                                    showTotal: (total) =>
                                        `${total} payroll records`,
                                }}
                                locale={{
                                    emptyText: (
                                        <Empty
                                            description={
                                                selectedYear?.id
                                                    ? "No payroll records found"
                                                    : "Please select an academic year"
                                            }
                                        />
                                    ),
                                }}
                            />
                        </Card>

                        {/* CHART */}

                        <Card className="chart-card">
                            <div className="section-heading">
                                <div>
                                    <Title level={4}>
                                        Monthly Payroll Summary
                                    </Title>

                                    <Text type="secondary">
                                        Total payroll paid by month
                                    </Text>
                                </div>
                            </div>

                            <div className="chart-wrapper">
                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >
                                    <BarChart data={chartData}>
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <Tooltip />

                                        <Bar
                                            dataKey="total"
                                            fill="#f97316"
                                            radius={[8, 8, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </>
                )}

                {/* CREATE / EDIT MODAL */}

                <Modal
                    open={openModal}
                    title={
                        <div className="modal-title">
                            <div className="modal-title-icon">
                                <DollarOutlined />
                            </div>

                            <div>
                                <Title level={4}>
                                    {editingPayroll
                                        ? "Edit Payroll"
                                        : "Create Payroll"}
                                </Title>

                                <Text type="secondary">
                                    {selectedYear?.name}
                                </Text>
                            </div>
                        </div>
                    }
                    onCancel={closeModal}
                    footer={null}
                    width={850}
                    centered
                    destroyOnClose
                >
                    <Alert
                        message="Select only one staff member"
                        description="A payroll record can belong to either a Teacher or Support Staff, not both."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <Row gutter={[16, 0]}>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Teacher"
                                    name="teacher_id"
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select teacher"
                                        disabled={
                                            selectedStaffType ===
                                            "support_staff"
                                        }
                                        optionFilterProp="label"
                                        onChange={handleTeacherChange}
                                        options={teachers.map((teacher) => ({
                                            label: teacher.full_name,
                                            value: teacher.id,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Support Staff"
                                    name="support_staff_id"
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select support staff"
                                        disabled={
                                            selectedStaffType === "teacher"
                                        }
                                        optionFilterProp="label"
                                        onChange={
                                            handleSupportStaffChange
                                        }
                                        options={supportStaff.map((staff) => ({
                                            label: staff.full_name,
                                            value: staff.id,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Payroll Month"
                                    name="month"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select a month.",
                                        },
                                    ]}
                                >
                                    <Select
                                        onChange={handleMonthChange}
                                        placeholder="Select month"
                                        options={MONTHS.map((month) => ({
                                            label: month,
                                            value: month,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Basic Salary"
                                    name="basic_salary"
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        prefix="$"
                                        readOnly
                                        min={0}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Bonus / Compensation"
                                    name="night_school_commission"
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        prefix="$"
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Salary Advance Deduction"
                                    name="salary_advance"
                                    extra={
                                        salaryAdvanceBalance > 0
                                            ? `Outstanding balance: $${salaryAdvanceBalance.toFixed(2)}`
                                            : "No outstanding salary advance"
                                    }
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        min={0}
                                        precision={2}
                                        prefix="$"
                                        disabled={salaryAdvanceLoading}
                                        onChange={(value) => {
                                            const deduction = Number(
                                                value || 0
                                            );

                                            if (
                                                deduction >
                                                salaryAdvanceBalance
                                            ) {
                                                message.warning(
                                                    "The deduction cannot be greater than the outstanding salary advance."
                                                );
                                            }
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="salary_advance_record"
                                    hidden
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Income Tax"
                                    name="income_tax"
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        prefix="$"
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Salary Deduction"
                                    name="salary_deduction"
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        prefix="$"
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                            </Col>

                            {/* NEW SALARY DEDUCTION REASON */}

                            <Col xs={24}>
                                <Form.Item
                                    shouldUpdate={(previous, current) =>
                                        previous.salary_deduction !==
                                        current.salary_deduction
                                    }
                                    noStyle
                                >
                                    {({ getFieldValue }) => {
                                        const deduction = Number(
                                            getFieldValue(
                                                "salary_deduction"
                                            ) || 0
                                        );

                                        return (
                                            <Form.Item
                                                label={
                                                    <Space>
                                                        <span>
                                                            Salary Deduction Reason
                                                        </span>

                                                        {deduction > 0 && (
                                                            <Tag color="red">
                                                                Required
                                                            </Tag>
                                                        )}
                                                    </Space>
                                                }
                                                name="salary_deduction_reason"
                                                rules={[
                                                    {
                                                        validator: (
                                                            _,
                                                            value
                                                        ) => {
                                                            if (
                                                                deduction > 0 &&
                                                                !String(
                                                                    value || ""
                                                                ).trim()
                                                            ) {
                                                                return Promise.reject(
                                                                    new Error(
                                                                        "Please explain why this salary deduction is being made."
                                                                    )
                                                                );
                                                            }

                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                                extra="Explain why money is being deducted from this employee's salary. This explanation will also be included in the teacher's payroll notification email."
                                            >
                                                <TextArea
                                                    placeholder="Enter the reason for this salary deduction..."
                                                    rows={4}
                                                    maxLength={2000}
                                                    showCount
                                                    allowClear
                                                />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            </Col>

                        </Row>

                        <div className="modal-footer">
                            <Button onClick={closeModal}>
                                Cancel
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                            >
                                {editingPayroll
                                    ? "Update Payroll"
                                    : "Create Payroll"}
                            </Button>
                        </div>
                    </Form>
                </Modal>

            </Content>
        </Layout>
    );
}

export default Payroll;





// import React, { useEffect, useMemo, useState } from "react";

// import {
//     Alert,
//     AutoComplete,
//     Button,
//     Card,
//     Col,
//     Empty,
//     Form,
//     Input,
//     InputNumber,
//     Layout,
//     Modal,
//     Row,
//     Select,
//     Space,
//     Statistic,
//     Table,
//     Tag,
//     Typography,
//     message,
// } from "antd";

// import {
//     DeleteOutlined,
//     EditOutlined,
//     FilePdfOutlined,
//     PlusOutlined,
//     PrinterOutlined,
//     ReloadOutlined,
//     SearchOutlined,
//     WalletOutlined,
//     DollarOutlined,
//     TeamOutlined,
//     RiseOutlined,
// } from "@ant-design/icons";

// import {
//     BarChart,
//     Bar,
//     XAxis,
//     YAxis,
//     Tooltip,
//     ResponsiveContainer,
// } from "recharts";
// import Swal from "sweetalert2";
// import { PDFDownloadLink } from "@react-pdf/renderer";
// import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";
// import SalaryAdvance from "./SalaryAdvance";
// import { useAcademicYear } from "../../AcademicYearContext";
// import api from "../../api/api";
// import "./Payroll.css";


// const {
//     Content,
// } = Layout;


// const {
//     Title,
//     Text,
// } = Typography;


// const MONTHS = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
// ];


// const initialFormData = {
//     teacher_id: null,
//     support_staff_id: null,
//     month: "",
//     basic_salary: 0,
//     night_school_commission: 0,
//     salary_advance: 0,
//     salary_advance_record: null,
//     salary_advance_balance: 0,
//     income_tax: 0,
//     salary_deduction: 0,
//     total_deductions: 0,
// };


// function Payroll() {
// const [salaryAdvanceBalance, setSalaryAdvanceBalance] = useState(0);
// const [salaryAdvanceRecord, setSalaryAdvanceRecord] = useState(null);
// const [salaryAdvanceLoading, setSalaryAdvanceLoading] = useState(false);

// const {selectedYear} = useAcademicYear();

// const [form] = Form.useForm();

// const [payrolls, setPayrolls] = useState([]);
// const [teachers, setTeachers] = useState([]);
// const [supportStaff, setSupportStaff] = useState([]);
// const [loading, setLoading] = useState(false);
// const [tableLoading, setTableLoading] = useState(false);
// const [openModal, setOpenModal] = useState(false);
// const [editingPayroll, setEditingPayroll] = useState(null);
// const [showSalaryAdvance, setShowSalaryAdvance] = useState(false);

// const [filters, setFilters] = useState({
//     search: "",
//     month: "",
// });

// const [selectedStaffType, setSelectedStaffType] = useState(null);
// const fetchSalaryAdvanceBalance = async ({
//     teacherId = null,
//     supportStaffId = null,
//     month = null,
// } = {}) => {
//     if (
//         !selectedYear?.id ||
//         !month ||
//         (!teacherId && !supportStaffId)
//     ) {
//         return;
//     }

//     setSalaryAdvanceLoading(true);

//     try {
//         const response = await api.get(
//             "/salary-advance-balance/",
//             {
//                 params: {
//                     teacher_id:
//                         teacherId || undefined,
//                     support_staff_id:
//                         supportStaffId || undefined,
//                     month,
//                     academic_year:
//                         selectedYear.id,
//                 },
//             }
//         );

//         console.log(
//             "SALARY ADVANCE RESPONSE:",
//             response.data
//         );

//         const balance = Number(
//             response.data?.remaining_balance || 0
//         );

//         const recordId =
//             response.data?.salary_advance_id || null;

//         setSalaryAdvanceBalance(balance);
//         setSalaryAdvanceRecord(recordId);

//         form.setFieldsValue({
//             salary_advance: balance,
//             salary_advance_record: recordId,
//         });
//     } catch (error) {
//         console.error(
//             "SALARY ADVANCE ERROR:",
//             error.response?.data || error
//         );

//         setSalaryAdvanceBalance(0);
//         setSalaryAdvanceRecord(null);

//         form.setFieldsValue({
//             salary_advance: 0,
//             salary_advance_record: null,
//         });
//     } finally {
//         setSalaryAdvanceLoading(false);
//     }
// };
//     /*
//     |--------------------------------------------------------------------------
//     | FETCH PAYROLLS
//     |--------------------------------------------------------------------------
//     */

//     const fetchPayrolls = async () => {

//         if (!selectedYear?.id) {

//             return;

//         }


//         setTableLoading(true);


//         try {

//             const response = await api.get(
//                 "/payrolls/",
//                 {
//                     params: {
//                         academic_year:
//                             selectedYear.id,
//                     },
//                 }
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setPayrolls(data);

//         } catch (error) {

//             console.error(
//                 "PAYROLL FETCH ERROR:",
//                 error.response?.data || error
//             );


//             message.error(
//                 "Failed to load payroll records."
//             );

//         } finally {

//             setTableLoading(false);

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH TEACHERS
//     |--------------------------------------------------------------------------
//     */

//     const fetchTeachers = async () => {

//         try {

//             const response = await api.get(
//                 "/api/users/teacher-data/"
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setTeachers(data);

//         } catch (error) {

//             console.error(
//                 "TEACHERS FETCH ERROR:",
//                 error
//             );


//             message.error(
//                 "Failed to load teachers."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH SUPPORT STAFF
//     |--------------------------------------------------------------------------
//     */

//     const fetchSupportStaff = async () => {

//         try {

//             const response = await api.get(
//                 "/supportstaff/"
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setSupportStaff(data);

//         } catch (error) {

//             console.error(
//                 "SUPPORT STAFF FETCH ERROR:",
//                 error
//             );


//             message.error(
//                 "Failed to load support staff."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | EFFECTS
//     |--------------------------------------------------------------------------
//     */

//     useEffect(() => {

//         if (selectedYear?.id) {

//             fetchPayrolls();

//         }

//     }, [
//         selectedYear?.id,
//     ]);


//     useEffect(() => {

//         fetchTeachers();

//         fetchSupportStaff();

//     }, []);


//     /*
//     |--------------------------------------------------------------------------
//     | FORMAT MONEY
//     |--------------------------------------------------------------------------
//     */

//     const formatMoney = (
//         value
//     ) => {

//         const number =
//             parseFloat(value || 0);


//         return `$${number.toLocaleString(
//             "en-US",
//             {
//                 minimumFractionDigits: 2,
//                 maximumFractionDigits: 2,
//             }
//         )}`;

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | STAFF NAME
//     |--------------------------------------------------------------------------
//     */

//     const getStaffName = (
//         payroll
//     ) => {

//         return (

//             payroll.teacher?.full_name ||

//             payroll.support_staff?.full_name ||

//             "Unknown Staff"

//         );

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | OPEN CREATE MODAL
//     |--------------------------------------------------------------------------
//     */

//     const openCreateModal = () => {

//         setEditingPayroll(null);

//         setSelectedStaffType(null);

//         form.resetFields();

//         form.setFieldsValue({

//             ...initialFormData,

//         });

//         setOpenModal(true);

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | OPEN EDIT MODAL
//     |--------------------------------------------------------------------------
//     */

//     const openEditModal = (
//         payroll
//     ) => {

//         setEditingPayroll(payroll);


//         const teacherId =
//             payroll.teacher?.id || null;


//         const supportStaffId =
//             payroll.support_staff?.id || null;


//         setSelectedStaffType(

//             teacherId

//                 ? "teacher"

//                 : supportStaffId

//                     ? "support_staff"

//                     : null

//         );


//         form.setFieldsValue({

//             teacher_id:
//                 teacherId,

//             support_staff_id:
//                 supportStaffId,

//             month:
//                 payroll.month || "",

//             basic_salary:
//                 Number(
//                     payroll.basic_salary || 0
//                 ),

//             night_school_commission:
//                 Number(
//                     payroll.night_school_commission || 0
//                 ),

//             salary_advance:
//                 Number(
//                     payroll.salary_advance || 0
//                 ),

//             income_tax:
//                 Number(
//                     payroll.income_tax || 0
//                 ),

//             salary_deduction:
//                 Number(
//                     payroll.salary_deduction || 0
//                 ),

//             total_deductions:
//                 Number(
//                     payroll.total_deductions || 0
//                 ),

//         });


//         setOpenModal(true);

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | CLOSE MODAL
//     |--------------------------------------------------------------------------
//     */

//     const closeModal = () => {

//         setOpenModal(false);

//         setEditingPayroll(null);

//         setSelectedStaffType(null);

//         form.resetFields();

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | GET TEACHER SALARY
//     |--------------------------------------------------------------------------
//     */

//     const fetchTeacherSalary = async (
//         teacherId
//     ) => {

//         if (!teacherId) {

//             return;

//         }


//         try {

//             const response = await api.get(
//                 `/teacher-salary/${teacherId}/`
//             );


//             form.setFieldValue(
//                 "basic_salary",
//                 Number(
//                     response.data.salary || 0
//                 )
//             );

//         } catch (error) {

//             console.error(
//                 "SALARY FETCH ERROR:",
//                 error
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | TEACHER CHANGE
//     |--------------------------------------------------------------------------
//     */
// const handleTeacherChange = async (teacherId) => {
//     setSelectedStaffType(
//         teacherId ? "teacher" : null
//     );

//     const currentMonth =
//         form.getFieldValue("month");

//     form.setFieldsValue({
//         teacher_id: teacherId,
//         support_staff_id: null,
//         salary_advance: 0,
//         salary_advance_record: null,
//     });

//     setSalaryAdvanceBalance(0);
//     setSalaryAdvanceRecord(null);

//     const teacher = teachers.find(
//         (item) => item.id === teacherId
//     );

//     if (teacher) {
//         form.setFieldsValue({
//             basic_salary: Number(
//                 teacher.salary || 0
//             ),
//         });
//     }

//     if (
//         teacherId &&
//         currentMonth
//     ) {
//         await fetchSalaryAdvanceBalance({
//             teacherId,
//             month: currentMonth,
//         });
//     }
// };

//     /*
//     |--------------------------------------------------------------------------
//     | SUPPORT STAFF CHANGE
//     |--------------------------------------------------------------------------
//     */
// const handleSupportStaffChange = async (staffId) => {
//     setSelectedStaffType(staffId ? "support_staff" : null);

//     const currentMonth = form.getFieldValue("month");

//     const staff = supportStaff.find((item) => item.id === staffId);

//     form.setFieldsValue({
//         support_staff_id: staffId || null,
//         teacher_id: null,
//         basic_salary: Number(staff?.salary || 0),
//         salary_advance: 0,
//         salary_advance_record: null,
//     });

//     if (staffId && currentMonth) {
//         await fetchSalaryAdvanceBalance({
//             supportStaffId: staffId,
//             month: currentMonth,
//         });
//     }
// };

// // Handle Month Change:
// const handleMonthChange = async (month) => {
//     const teacherId = form.getFieldValue("teacher_id");
//     const supportStaffId = form.getFieldValue("support_staff_id");

//     if (teacherId) {
//         await fetchSalaryAdvanceBalance({
//             teacherId,
//             month,
//         });
//     }

//     if (supportStaffId) {
//         await fetchSalaryAdvanceBalance({
//             supportStaffId,
//             month,
//         });
//     }
// };

//     /*
//     |--------------------------------------------------------------------------
//     | SUBMIT PAYROLL
//     |--------------------------------------------------------------------------
//     */
// const handleSubmit = async (values) => {
//     if (!values.teacher_id && !values.support_staff_id) {
//         message.error("Please select a teacher or support staff.");
//         return;
//     }

//     if (!selectedYear?.id) {
//         message.error("Please select an academic year.");
//         return;
//     }
// const deduction = Number(
//     values.salary_advance || 0
// );

// const outstandingBalance = Number(
//     salaryAdvanceBalance || 0
// );

// if (deduction > outstandingBalance) {
//     message.error(
//         "Salary advance deduction cannot be greater than the outstanding balance."
//     );

//     return;
// }

//     setLoading(true);

//     try {
//   const payload = {
//     teacher_id: values.teacher_id || null,
//     support_staff_id: values.support_staff_id || null,
//     month: values.month,
//     basic_salary: Number(values.basic_salary || 0),
//     night_school_commission: Number(values.night_school_commission || 0),
//     salary_advance: deduction,
//     salary_advance_record: values.salary_advance_record || salaryAdvanceRecord || null,
//     income_tax: Number(values.income_tax || 0),
//     salary_deduction: Number(values.salary_deduction || 0),
// };

//         const response = editingPayroll
//             ? await api.put(
//                 `/payrolls/${editingPayroll.id}/`,
//                 payload
//             )
//             : await api.post(
//                 "/payrolls/",
//                 payload
//             );

//         if (editingPayroll) {
//             setPayrolls((previous) =>
//                 previous.map((item) =>
//                     item.id === editingPayroll.id
//                         ? response.data
//                         : item
//                 )
//             );

//             message.success(
//                 "Payroll updated successfully."
//             );
//         } else {
//             setPayrolls((previous) => [
//                 response.data,
//                 ...previous,
//             ]);

//             message.success(
//                 "Payroll created successfully."
//             );
//         }

//         closeModal();
//     } catch (error) {
//         console.error(
//             "PAYROLL SUBMIT ERROR:",
//             error.response?.data || error
//         );

//         Swal.fire({
//             icon: "error",
//             title: "Payroll Error",
//             text:
//                 typeof error.response?.data === "object"
//                     ? JSON.stringify(
//                         error.response.data
//                     )
//                     : (
//                         error.response?.data ||
//                         "Unable to save payroll."
//                     ),
//         });
//     } finally {
//         setLoading(false);
//     }
// };
//     /*
//     |--------------------------------------------------------------------------
//     | DELETE PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handleDelete = async (
//         id
//     ) => {

//         const confirmation =
//             await Swal.fire({

//                 title:
//                     "Delete Payroll?",

//                 text:
//                     "This payroll record will be permanently deleted.",

//                 icon:
//                     "warning",

//                 showCancelButton:
//                     true,

//                 confirmButtonText:
//                     "Delete",

//                 confirmButtonColor:
//                     "#f97316",

//             });


//         if (
//             !confirmation.isConfirmed
//         ) {

//             return;

//         }


//         try {

//             await api.delete(
//                 `/payrolls/${id}/`
//             );


//             setPayrolls(
//                 (previous) =>

//                     previous.filter(
//                         (item) =>
//                             item.id !== id
//                     )

//             );


//             message.success(
//                 "Payroll deleted successfully."
//             );

//         } catch (error) {

//             console.error(
//                 error
//             );


//             message.error(
//                 "Failed to delete payroll."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | PRINT PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handlePrint = (
//         payroll
//     ) => {

//         const newWindow =
//             window.open(
//                 "",
//                 "_blank"
//             );


//         if (!newWindow) {

//             return;

//         }


//         const staffName =
//             getStaffName(payroll);


//         newWindow.document.write(`

//             <html>

//                 <head>

//                     <title>
//                         Payroll - ${staffName}
//                     </title>

//                     <style>

//                         body {
//                             font-family: Arial, sans-serif;
//                             padding: 30px;
//                             color: #111;
//                         }

//                         h1 {
//                             text-align: center;
//                         }

//                         table {
//                             width: 100%;
//                             border-collapse: collapse;
//                             margin-top: 25px;
//                         }

//                         th,
//                         td {
//                             border: 1px solid #ddd;
//                             padding: 12px;
//                             text-align: left;
//                         }

//                         th {
//                             background: #111;
//                             color: white;
//                         }

//                         .total {
//                             font-weight: bold;
//                             font-size: 18px;
//                         }

//                     </style>

//                 </head>

//                 <body>

//                     <h1>
//                         Payroll Slip
//                     </h1>

//                     <p>
//                         <strong>Staff:</strong>
//                         ${staffName}
//                     </p>

//                     <p>
//                         <strong>Month:</strong>
//                         ${payroll.month}
//                     </p>

//                     <p>
//                         <strong>Academic Year:</strong>
//                         ${payroll.academic_year?.name || ""}
//                     </p>

//                     <table>

//                         <tr>
//                             <th>Basic Salary</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.basic_salary
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Bonus / Compensation</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.night_school_commission
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Salary Advance</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.salary_advance
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Income Tax</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.income_tax
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Social Security</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.salary_deduction
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Total Deductions</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.total_deductions
//                                 )}
//                             </td>
//                         </tr>

//                         <tr class="total">
//                             <th>Total Paid</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.total_paid
//                                 )}
//                             </td>
//                         </tr>

//                     </table>

//                 </body>

//             </html>

//         `);


//         newWindow.document.close();

//         newWindow.print();

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FILTERED PAYROLLS
//     |--------------------------------------------------------------------------
//     */

//     const filteredPayrolls =
//         useMemo(() => {

//             return payrolls.filter(
//                 (payroll) => {

//                     const staffName =
//                         getStaffName(payroll);


//                     const matchesSearch =
//                         !filters.search ||

//                         staffName
//                             .toLowerCase()
//                             .includes(
//                                 filters.search
//                                     .toLowerCase()
//                             );


//                     const matchesMonth =
//                         !filters.month ||

//                         payroll.month
//                             ?.toLowerCase() ===
//                         filters.month
//                             .toLowerCase();


//                     return (

//                         matchesSearch &&

//                         matchesMonth

//                     );

//                 }
//             );

//         }, [
//             payrolls,
//             filters,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | STATISTICS
//     |--------------------------------------------------------------------------
//     */

//     const statistics =
//         useMemo(() => {

//             const totalPayroll =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.total_paid || 0
//                         ),

//                     0
//                 );


//             const totalDeductions =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.total_deductions || 0
//                         ),

//                     0
//                 );


//             const totalAdvances =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.salary_advance || 0
//                         ),

//                     0
//                 );


//             return {

//                 totalPayroll,

//                 totalDeductions,

//                 totalAdvances,

//                 staffCount:
//                     filteredPayrolls.length,

//             };

//         }, [
//             filteredPayrolls,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | CHART DATA
//     |--------------------------------------------------------------------------
//     */

//     const chartData =
//         useMemo(() => {

//             const grouped =
//                 filteredPayrolls.reduce(
//                     (
//                         accumulator,
//                         item
//                     ) => {

//                         const key =
//                             item.month;


//                         if (
//                             !accumulator[key]
//                         ) {

//                             accumulator[key] = {

//                                 month:
//                                     key,

//                                 total:
//                                     0,

//                             };

//                         }


//                         accumulator[key].total +=
//                             Number(
//                                 item.total_paid || 0
//                             );


//                         return accumulator;

//                     },

//                     {}
//                 );


//             return Object.values(
//                 grouped
//             );

//         }, [
//             filteredPayrolls,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | TABLE COLUMNS
//     |--------------------------------------------------------------------------
//     */

//     const columns = [

//         {

//             title:
//                 "Staff Name",

//             key:
//                 "staff",

//             fixed:
//                 "left",

//             width:
//                 220,

//             render:
//                 (_, record) => (

//                     <div className="staff-cell">

//                         <div className="staff-avatar">

//                             {getStaffName(
//                                 record
//                             )
//                                 .charAt(0)
//                                 .toUpperCase()}

//                         </div>

//                         <div>

//                             <Text
//                                 strong
//                             >

//                                 {getStaffName(
//                                     record
//                                 )}

//                             </Text>

//                             <br />

//                             <Text
//                                 type="secondary"
//                                 className="staff-type"
//                             >

//                                 {record.teacher
//                                     ? "Teacher"
//                                     : "Support Staff"}

//                             </Text>

//                         </div>

//                     </div>

//                 ),

//         },


//         {

//             title:
//                 "Month",

//             dataIndex:
//                 "month",

//             key:
//                 "month",

//             width:
//                 120,

//             render:
//                 (month) => (

//                     <Tag
//                         color="orange"
//                     >

//                         {month}

//                     </Tag>

//                 ),

//         },


//         {

//             title:
//                 "Academic Year",

//             key:
//                 "academic_year",

//             width:
//                 150,

//             render:
//                 (_, record) =>

//                     record.academic_year?.name || "-",

//         },


//         {

//             title:
//                 "Basic Salary",

//             dataIndex:
//                 "basic_salary",

//             key:
//                 "basic_salary",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Bonus",

//             dataIndex:
//                 "night_school_commission",

//             key:
//                 "bonus",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Deductions",

//             dataIndex:
//                 "total_deductions",

//             key:
//                 "deductions",

//             width:
//                 140,

//             render:
//                 (value) => (

//                     <Text
//                         type="danger"
//                         strong
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Advance",

//             dataIndex:
//                 "salary_advance",

//             key:
//                 "advance",

//             width:
//                 140,

//             render:
//                 (value) => (

//                     <Text
//                         type="danger"
//                         strong
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Income Tax",

//             dataIndex:
//                 "income_tax",

//             key:
//                 "income_tax",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Social Security",

//             dataIndex:
//                 "salary_deduction",

//             key:
//                 "social_security",

//             width:
//                 150,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Total Paid",

//             dataIndex:
//                 "total_paid",

//             key:
//                 "total_paid",

//             width:
//                 150,

//             render:
//                 (value) => (

//                     <Text
//                         strong
//                         className="total-paid"
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Actions",

//             key:
//                 "actions",

//             fixed:
//                 "right",

//             width:
//                 160,

//             render:
//                 (_, record) => (

//                     <Space>

//                         <Button

//                             type="text"

//                             icon={
//                                 <EditOutlined />
//                             }

//                             onClick={() =>
//                                 openEditModal(
//                                     record
//                                 )
//                             }

//                         />

//                         <Button

//                             type="text"

//                             danger

//                             icon={
//                                 <DeleteOutlined />
//                             }

//                             onClick={() =>
//                                 handleDelete(
//                                     record.id
//                                 )
//                             }

//                         />

//                         <Button

//                             type="text"

//                             icon={
//                                 <PrinterOutlined />
//                             }

//                             onClick={() =>
//                                 handlePrint(
//                                     record
//                                 )
//                             }

//                         />

//                     </Space>

//                 ),

//         },

//     ];


//     return (

//         <Layout
//             className="payroll-page"
//         >

//             <Content
//                 className="payroll-content"
//             >

//                 {/* HEADER */}

//                 <div
//                     className="payroll-header"
//                 >

//                     <div>

//                         <Text
//                             className="eyebrow"
//                         >

//                             FINANCE / PAYROLL

//                         </Text>


//                         <Title
//                             level={2}
//                             className="page-title"
//                         >

//                             Staff Payroll Management

//                         </Title>


//                         <Text
//                             type="secondary"
//                         >

//                             Manage staff compensation, deductions,
//                             salary advances and payroll reporting.

//                         </Text>

//                     </div>


//                     <div
//                         className="year-badge"
//                     >

//                         <Text>
//                             Academic Year
//                         </Text>

//                         <strong>
//                             {selectedYear?.name || "Not Selected"}
//                         </strong>

//                     </div>

//                 </div>


//                 {/* STATISTICS */}

//                 <Row
//                     gutter={[
//                         16,
//                         16,
//                     ]}
//                     className="stats-row"
//                 >

//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card orange-card"
//                         >

//                             <Statistic

//                                 title="Total Payroll"

//                                 value={
//                                     statistics.totalPayroll
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <DollarOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card dark-card"
//                         >

//                             <Statistic

//                                 title="Staff Records"

//                                 value={
//                                     statistics.staffCount
//                                 }

//                                 prefix={
//                                     <TeamOutlined />
//                                 }

//                             />

//                             <TeamOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card light-card"
//                         >

//                             <Statistic

//                                 title="Total Deductions"

//                                 value={
//                                     statistics.totalDeductions
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <RiseOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card black-card"
//                         >

//                             <Statistic

//                                 title="Salary Advances"

//                                 value={
//                                     statistics.totalAdvances
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <WalletOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>

//                 </Row>


//                 {/* SALARY ADVANCE */}

//                 {showSalaryAdvance ? (

//                     <Card>

//                         <Button

//                             icon={
//                                 <ReloadOutlined />
//                             }

//                             onClick={() =>
//                                 setShowSalaryAdvance(
//                                     false
//                                 )
//                             }

//                             className="back-button"

//                         >

//                             Back To Payroll

//                         </Button>


//                         <SalaryAdvance />

//                     </Card>

//                 ) : (

//                     <>

//                         {/* TOOLBAR */}

//                         <Card
//                             className="toolbar-card"
//                         >

//                             <Row
//                                 gutter={[
//                                     12,
//                                     12,
//                                 ]}
//                                 align="middle"
//                             >

//                                 <Col
//                                     xs={24}
//                                     md={8}
//                                 >

//                                     <Input

//                                         size="large"

//                                         prefix={
//                                             <SearchOutlined />
//                                         }

//                                         placeholder="Search staff name..."

//                                         value={
//                                             filters.search
//                                         }

//                                         onChange={(
//                                             event
//                                         ) =>

//                                             setFilters(
//                                                 (
//                                                     previous
//                                                 ) => ({

//                                                     ...previous,

//                                                     search:
//                                                         event
//                                                             .target
//                                                             .value,

//                                                 })
//                                             )

//                                         }

//                                         allowClear

//                                     />

//                                 </Col>


//                                 <Col
//                                     xs={24}
//                                     sm={12}
//                                     md={5}
//                                 >

//                                     <Select

//                                         size="large"

//                                         placeholder="Filter by month"

//                                         value={
//                                             filters.month ||
//                                             undefined
//                                         }

//                                         onChange={(
//                                             value
//                                         ) =>

//                                             setFilters(
//                                                 (
//                                                     previous
//                                                 ) => ({

//                                                     ...previous,

//                                                     month:
//                                                         value ||
//                                                         "",

//                                                 })
//                                             )

//                                         }

//                                         allowClear

//                                         options={
//                                             MONTHS.map(
//                                                 (
//                                                     month
//                                                 ) => ({

//                                                     label:
//                                                         month,

//                                                     value:
//                                                         month,

//                                                 })
//                                             )
//                                         }

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                     />

//                                 </Col>


//                                 <Col
//                                     xs={24}
//                                     sm={12}
//                                     md={11}
//                                 >

//                                     <Space
//                                         wrap
//                                         className="toolbar-actions"
//                                     >

//                                         <Button

//                                             icon={
//                                                 <WalletOutlined />
//                                             }

//                                             onClick={() =>
//                                                 setShowSalaryAdvance(
//                                                     true
//                                                 )
//                                             }

//                                         >

//                                             Salary Advances

//                                         </Button>


//                                         <Button

//                                             type="primary"

//                                             icon={
//                                                 <PlusOutlined />
//                                             }

//                                             onClick={
//                                                 openCreateModal
//                                             }

//                                         >

//                                             Create Payroll

//                                         </Button>


//                                         <PDFDownloadLink

//                                             document={

//                                                 <PayrollSlipGenerationPDF

//                                                     payrolls={
//                                                         filteredPayrolls
//                                                     }

//                                                     month={
//                                                         filters.month
//                                                     }

//                                                     academicYear={
//                                                         selectedYear?.name
//                                                     }

//                                                 />

//                                             }

//                                             fileName={

//                                                 `Payslips_${

//                                                     filters.month ||
//                                                     "All_Months"

//                                                 }_${

//                                                     selectedYear?.name ||
//                                                     "Academic_Year"

//                                                 }.pdf`

//                                             }

//                                         >

//                                             {({
//                                                 loading,
//                                             }) => (

//                                                 <Button

//                                                     icon={
//                                                         <FilePdfOutlined />
//                                                     }

//                                                     danger

//                                                 >

//                                                     {loading

//                                                         ? "Generating..."

//                                                         : "Download PDF"}

//                                                 </Button>

//                                             )}

//                                         </PDFDownloadLink>


//                                     </Space>

//                                 </Col>

//                             </Row>

//                         </Card>


//                         {/* TABLE */}

//                         <Card
//                             className="table-card"
//                         >

//                             <Table

//                                 rowKey="id"

//                                 loading={
//                                     tableLoading
//                                 }

//                                 columns={
//                                     columns
//                                 }

//                                 dataSource={
//                                     filteredPayrolls
//                                 }

//                                 scroll={{
//                                     x:
//                                         1650,
//                                 }}

//                                 pagination={{
//                                     pageSize:
//                                         10,

//                                     showSizeChanger:
//                                         true,

//                                     pageSizeOptions:
//                                         [
//                                             "10",
//                                             "20",
//                                             "50",
//                                             "100",
//                                         ],

//                                     showTotal:
//                                         (
//                                             total
//                                         ) =>

//                                             `${total} payroll records`,

//                                 }}

//                                 locale={{

//                                     emptyText: (

//                                         <Empty

//                                             description={
//                                                 selectedYear?.id

//                                                     ? "No payroll records found"

//                                                     : "Please select an academic year"

//                                             }

//                                         />

//                                     ),

//                                 }}

//                             />

//                         </Card>


//                         {/* CHART */}

//                         <Card
//                             className="chart-card"
//                         >

//                             <div
//                                 className="section-heading"
//                             >

//                                 <div>

//                                     <Title
//                                         level={4}
//                                     >

//                                         Monthly Payroll Summary

//                                     </Title>

//                                     <Text
//                                         type="secondary"
//                                     >

//                                         Total payroll paid by month

//                                     </Text>

//                                 </div>

//                             </div>


//                             <div
//                                 className="chart-wrapper"
//                             >

//                                 <ResponsiveContainer
//                                     width="100%"
//                                     height="100%"
//                                 >

//                                     <BarChart
//                                         data={
//                                             chartData
//                                         }
//                                     >

//                                         <XAxis
//                                             dataKey="month"
//                                         />

//                                         <YAxis />

//                                         <Tooltip />

//                                         <Bar
//                                             dataKey="total"
//                                             fill="#f97316"
//                                             radius={[
//                                                 8,
//                                                 8,
//                                                 0,
//                                                 0,
//                                             ]}
//                                         />

//                                     </BarChart>

//                                 </ResponsiveContainer>

//                             </div>

//                         </Card>

//                     </>

//                 )}


//                 {/* CREATE / EDIT MODAL */}

//                 <Modal

//                     open={
//                         openModal
//                     }

//                     title={

//                         <div
//                             className="modal-title"
//                         >

//                             <div
//                                 className="modal-title-icon"
//                             >

//                                 <DollarOutlined />

//                             </div>


//                             <div>

//                                 <Title
//                                     level={4}
//                                 >

//                                     {editingPayroll

//                                         ? "Edit Payroll"

//                                         : "Create Payroll"}

//                                 </Title>

//                                 <Text
//                                     type="secondary"
//                                 >

//                                     {selectedYear?.name}

//                                 </Text>

//                             </div>

//                         </div>

//                     }

//                     onCancel={
//                         closeModal
//                     }

//                     footer={null}

//                     width={
//                         850
//                     }

//                     centered

//                     destroyOnClose

//                 >

//                     <Alert

//                         message="Select only one staff member"

//                         description="A payroll record can belong to either a Teacher or Support Staff, not both."

//                         type="info"

//                         showIcon

//                         style={{
//                             marginBottom:
//                                 24,
//                         }}

//                     />


//                     <Form

//                         form={
//                             form
//                         }

//                         layout="vertical"

//                         onFinish={
//                             handleSubmit
//                         }

//                     >

//                         <Row
//                             gutter={[
//                                 16,
//                                 0,
//                             ]}
//                         >

//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item
//                                     label="Teacher"
//                                     name="teacher_id"
//                                 >

//                                     <Select

//                                         showSearch

//                                         allowClear

//                                         placeholder="Select teacher"

//                                         disabled={
//                                             selectedStaffType ===
//                                             "support_staff"
//                                         }

//                                         optionFilterProp="label"

//                                         onChange={
//                                             handleTeacherChange
//                                         }

//                                         options={

//                                             teachers.map(
//                                                 (
//                                                     teacher
//                                                 ) => ({

//                                                     label:
//                                                         teacher.full_name,

//                                                     value:
//                                                         teacher.id,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item
//                                     label="Support Staff"
//                                     name="support_staff_id"
//                                 >

//                                     <Select

//                                         showSearch

//                                         allowClear

//                                         placeholder="Select support staff"

//                                         disabled={
//                                             selectedStaffType ===
//                                             "teacher"
//                                         }

//                                         optionFilterProp="label"

//                                         onChange={
//                                             handleSupportStaffChange
//                                         }

//                                         options={

//                                             supportStaff.map(
//                                                 (
//                                                     staff
//                                                 ) => ({

//                                                     label:
//                                                         staff.full_name,

//                                                     value:
//                                                         staff.id,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                <Form.Item
//     label="Payroll Month"
//     name="month"
//     rules={[
//         {
//             required: true,
//             message: "Please select a month.",
//         },
//     ]}
// >
// <Select
//     onChange={handleMonthChange}
//     options={MONTHS.map((month) => ({
//         label: month,
//         value: month,
//     }))}
// />
// </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Basic Salary"

//                                     name="basic_salary"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         readOnly

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Bonus / Compensation"

//                                     name="night_school_commission"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                             <Form.Item
//     label="Salary Advance Deduction"
//     name="salary_advance"
//     extra={
//         salaryAdvanceBalance > 0
//             ? `Outstanding balance: $${salaryAdvanceBalance.toFixed(2)}`
//             : "No outstanding salary advance"
//     }
// >
//     <InputNumber
//         style={{
//             width: "100%",
//         }}
//         min={0}
//         precision={2}
//         prefix="$"
//         disabled={salaryAdvanceLoading}
//         onChange={(value) => {
//             const deduction = Number(value || 0);

//             if (deduction > salaryAdvanceBalance) {
//                 message.warning(
//                     "The deduction cannot be greater than the outstanding salary advance."
//                 );
//             }
//         }}
//     />
// </Form.Item>
// <Form.Item
//     name="salary_advance_record"
//     hidden
// >
//     <Input />
// </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Income Tax"

//                                     name="income_tax"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Salary Deduction"

//                                     name="salary_deduction"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>

//                         </Row>


//                         <div
//                             className="modal-footer"
//                         >

//                             <Button
//                                 onClick={
//                                     closeModal
//                                 }
//                             >

//                                 Cancel

//                             </Button>


//                             <Button

//                                 type="primary"

//                                 htmlType="submit"

//                                 loading={
//                                     loading
//                                 }

//                             >

//                                 {editingPayroll

//                                     ? "Update Payroll"

//                                     : "Create Payroll"}

//                             </Button>

//                         </div>

//                     </Form>

//                 </Modal>

//             </Content>

//         </Layout>

//     );

// }


// export default Payroll;